import { SupabaseClient } from '@supabase/supabase-js';
import { PaginationOptions, PaginatedResponse } from '../types/pagination.types';
import { applyPagination } from '../utils/pagination.utils';

export abstract class BaseRepository<T, TInsert, TUpdate> {
  protected tableName: string;

  constructor(
    protected supabase: SupabaseClient,
    tableName: string
  ) {
    this.tableName = tableName;
  }

  async findById(id: string): Promise<T | null> {
    const { data, error } = await this.supabase
      .from(this.tableName)
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;
    return data as T;
  }

  async findAll(options?: PaginationOptions): Promise<T[] | PaginatedResponse<T>> {
    const query = this.supabase.from(this.tableName).select('*');
    return applyPagination<any, T>(query, options);
  }

  async create(data: TInsert): Promise<T> {
    const { data: created, error } = await this.supabase
      .from(this.tableName)
      .insert(data)
      .select()
      .single();

    if (error) throw error;
    return created as T;
  }

  async update(id: string, data: TUpdate): Promise<T> {
    const { data: updated, error } = await this.supabase
      .from(this.tableName)
      .update(data)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return updated as T;
  }

  async delete(id: string): Promise<void> {
    const { error } = await this.supabase
      .from(this.tableName)
      .delete()
      .eq('id', id);

    if (error) throw error;
  }

  async exists(id: string): Promise<boolean> {
    const { count, error } = await this.supabase
      .from(this.tableName)
      .select('id', { count: 'exact', head: true })
      .eq('id', id);

    if (error) throw error;
    return count !== null && count > 0;
  }
}
